import {useState, useCallback, useEffect, useMemo} from 'react'
import {WeekSchedule} from '../../../state/rest'
import {parseISOTimeToDate} from '../../Atoms/Utils'

type RHFBridge = {watch: any; setValue: any}

export function useWeekScheduleField(form: RHFBridge, saved?: WeekSchedule[]) {
  const todayMonZero = () => (new Date().getDay() + 6) % 7

  const newSchedule = (): WeekSchedule => ({
    weekdays: [todayMonZero()],
    from: '09:00:00',
    to: '17:00:00'
  })

  const initial: WeekSchedule[] = (() => {
    const fromForm = (form.watch && form.watch('schedules')) as WeekSchedule[] | undefined
    if (Array.isArray(fromForm) && fromForm.length) return fromForm
    if (Array.isArray(saved) && saved.length) return saved
    return [newSchedule()]
  })()

  const [schedules, setSchedules] = useState<WeekSchedule[]>(initial)

  useEffect(() => {
    form.setValue('schedules', schedules)
  }, [schedules])

  useEffect(() => {
    if (saved && saved.length && schedules.length === 0) {
      setSchedules(saved)
    }
  }, [schedules.length, JSON.stringify(saved)])

  const sanitizeWeekdays = (days?: number[]) => {
    const set = new Set<number>()
    ;(days ?? []).forEach(d => {
      const v = Number(d)
      if (v >= 0 && v <= 6) set.add(v)
    })
    return Array.from(set).sort((a, b) => a - b)
  }

  const isValidSchedule = (s: WeekSchedule) => {
    if (!s.from || !s.to) return false
    const fromD = parseISOTimeToDate(s.from)
    const toD = parseISOTimeToDate(s.to)
    if (!(fromD < toD)) return false
    const days = sanitizeWeekdays(s.weekdays)
    return days.length > 0
  }

  const isValid = schedules.every(isValidSchedule)
  const checkValidity = useCallback(() => isValid, [isValid])

  const handleAdd = useCallback(() => {
    setSchedules(prev => [...prev, newSchedule()])
  }, [])

  const handleRemove = useCallback((index: number) => {
    setSchedules(prev => prev.filter((_, i) => i !== index))
  }, [])

  const handleChange = useCallback((next: WeekSchedule, index: number) => {
    setSchedules(prev => {
      const cur = prev[index]
      if (!cur) return prev

      const nextNorm: WeekSchedule = {
        weekdays: sanitizeWeekdays(next.weekdays),
        from: next.from,
        to: next.to
      }

      const sameDays =
        cur.weekdays?.length === nextNorm.weekdays?.length && cur.weekdays?.every((v, i) => v === nextNorm.weekdays![i])

      const unchanged = sameDays && cur.from === nextNorm.from && cur.to === nextNorm.to
      if (unchanged) return prev

      const copy = prev.slice()
      copy[index] = nextNorm
      return copy
    })
  }, [])

  return {schedules, handleChange, handleAdd, handleRemove, checkValidity, isValid}
}
